import React from 'react';

import { formatToFixedMoneyWithLocale } from 'Helpers/formatService';

import Modal from 'CommonComponents/Modal';
import ModalFooter from 'CommonComponents/ModalFooter';
import ModalHeader from 'CommonComponents/ModalHeader';

/**
 * What submitting the queue is about to do, shown before anything is sent.
 *
 * Deliberately carries no DC / EA split: every commitment raised from this queue
 * is a Direct Commitment, so a breakdown would be a column of zeroes and a
 * question the reconciler cannot act on.
 */
export default function SendForCommitmentSubmitModal({ modal, count, commitmentCount, total, overBudgetCount, overBudgetTotal, submitting, onConfirm }) {
    const close = () => modal.hide();

    return (
        <Modal id="send-for-commitment-submit-modal" modal={modal}>
            <ModalHeader close={close} subtitle={formatToFixedMoneyWithLocale(total)}>
                {$t('Submit {{count}} for commitment', { count })}
            </ModalHeader>

            <div className="tw-mb-6 tw-space-y-3 tw-text-sm tw-text-charcoal">
                {commitmentCount < count && (
                    // Said before the approval wording below, because it changes what
                    // that wording is about: the approvals are per commitment, and
                    // combining is the one thing on this screen that makes those two
                    // numbers differ.
                    <p className="tw-mb-0 tw-font-medium">{$t('These are combined into {{count}} commitment(s).', { count: commitmentCount })}</p>
                )}
                <p className="tw-mb-0">
                    {$t(
                        'On submission, these items follow the approval process configured for your account. Approved items become normal committed spend, with a contract record created against each supplier, available in the Contract Management module.',
                    )}
                </p>
                <p className="tw-mb-0 tw-text-charcoal-75">{$t('Where no approval workflow applies, the commitment is created immediately.')}</p>

                {/* Committing over a budget line raises it. Said here rather than only on
                    the row, because this is the last point at which the reconciler can
                    stop — and the consequence is a change to the budget, not just to the
                    commitment they were looking at.

                    Both routes are named because the queue cannot tell which will run:
                    that turns on the approval settings for the line's own department or
                    cost centre and on the thresholds above them, not on anything the row
                    carries. Promising one would be wrong on any event configured the
                    other way. */}
                {overBudgetCount > 0 && (
                    <p className="tw-mb-0 tw-rounded tw-bg-amber-100 tw-px-3 tw-py-2 tw-text-amber-700">
                        {$t(
                            '{{count}} of these commit more than their budget line has left, {{total}} over in total. Submitting them will increase those budget lines — automatically, or through the budget change process where approval for budget increases is switched on, in which case the commitment waits until that change is approved.',
                            { count: overBudgetCount, total: formatToFixedMoneyWithLocale(overBudgetTotal) },
                        )}
                    </p>
                )}
            </div>

            <ModalFooter
                submitButtonVariant="primary"
                cancelButtonText={$t('Cancel')}
                primaryButtonText={submitting ? $t('Submitting…') : $t('Confirm and submit')}
                primaryButtonDisabled={submitting}
                isSubmitting={submitting}
                onCancel={close}
                handleSubmit={onConfirm}
            />
        </Modal>
    );
}
